import React from "react";
import { MultiSelectDropdown } from "../../../components/custom/multi-select-dropdown";
import SelectDropdownAsyncPaginate from "../../../components/custom/select-dropdown-async-paginate";
import { ApiConfig, API_USER_ROUTER } from "../../../services/api-config";
import { Department } from "../../department/services/department-service";
import {
  ITEM_DEBT_MAX_LEVEL,
  ItemDebtPenaltyConfig,
  ItemDebtPenaltyLevel,
  ItemDebtPenaltyUser,
} from "../item-debt-penalty-config";

interface ItemDebtPenaltyEditorProps {
  value: ItemDebtPenaltyConfig;
  departments: Department[];
  onChange: (next: ItemDebtPenaltyConfig) => void;
  /** Tăng sau khi tải cấu hình từ server → ô chọn cá nhân nạp lại danh sách đã chọn (không đổi khi người dùng chọn). */
  reloadKey?: number;
}

const ROW = "grid grid-cols-10 gap-4 items-center py-4";
const NUM_INPUT =
  "w-full border rounded-lg px-3 py-2 text-right focus:outline-none focus:ring-1 focus:ring-green-500";

/**
 * Phạt nợ đồ quá hạn theo nấc: khoảng nhảy nấc + mỗi nấc chọn bộ phận / cá nhân bị phạt THÊM (ngoài NV giặt
 * của đơn — luôn bị phạt). Bộ phận & cá nhân chỉ áp người thuộc chi nhánh sản xuất của đơn — chỉ định nhiều
 * tổ trưởng ở nhiều cửa hàng thì đơn cửa hàng nào chỉ phạt tổ trưởng cửa hàng đó.
 */
export const ItemDebtPenaltyEditor: React.FC<ItemDebtPenaltyEditorProps> = ({
  value,
  departments,
  onChange,
  reloadKey = 0,
}) => {
  const patchLevel = (index: number, patch: Partial<ItemDebtPenaltyLevel>) => {
    onChange({
      ...value,
      levels: value.levels.map((lv, i) => (i === index ? { ...lv, ...patch } : lv)),
    });
  };

  const escalationOn = value.escalation_interval_hours > 0;

  return (
    <>
      <div className={ROW}>
        <div className="col-span-7">
          <div className="text-sm font-medium text-slate-700">Khoảng nhảy nấc phạt (giờ)</div>
          <div className="text-xs text-slate-400 mt-0.5">
            Quá hạn trả mà vẫn chưa trả bù thì cứ sau khoảng này hệ thống phạt thêm 1 nấc (Lần 1 → 2 → 3), sau Lần{" "}
            {ITEM_DEBT_MAX_LEVEL} dừng. Đặt <b>0</b> để chỉ phạt Lần 1. Hệ thống nhắc người sắp bị phạt, giám sát và
            người quản lý đơn của chi nhánh trước khoảng 2 giờ (không nhắc nếu hạn trả / khoảng nhảy nấc dưới 2 giờ).
          </div>
        </div>
        <div className="col-span-3">
          <input
            type="number"
            min="0"
            value={value.escalation_interval_hours || ""}
            onChange={(e) => onChange({ ...value, escalation_interval_hours: Math.max(0, Number(e.target.value) || 0) })}
            placeholder="0 (chỉ phạt Lần 1)"
            className={NUM_INPUT}
          />
        </div>
      </div>

      <div className="py-4">
        <div className="text-sm font-medium text-slate-700">Người chịu phạt theo từng lần</div>
        <div className="text-xs text-slate-400 mt-0.5 mb-3">
          <b>NV giặt của đơn luôn bị phạt ở mọi lần.</b> Chọn thêm bộ phận và/hoặc cá nhân (VD tổ trưởng, ca trưởng) —
          chỉ người thuộc <b>chi nhánh sản xuất</b> của đơn mới bị phạt (đơn điều chuyển: chi nhánh đã giặt). Mỗi
          người 1 phiếu, điểm phạt theo mục "Nợ đồ quá hạn" ở trên.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {value.levels.slice(0, ITEM_DEBT_MAX_LEVEL).map((lv, i) => {
            const disabled = i > 0 && !escalationOn;
            const selectedDepts = departments.filter((d) => lv.department_ids.includes(d.id));
            return (
              <div
                key={i}
                className={`border border-slate-200 rounded-lg p-3 bg-white space-y-2 ${disabled ? "opacity-50" : ""}`}
              >
                <div className="text-xs font-semibold text-slate-600">
                  Lần {i + 1}
                  {disabled && <span className="font-normal text-slate-400"> — cần khoảng nhảy nấc &gt; 0</span>}
                </div>
                <div>
                  <label className="text-[11px] text-slate-400 block mb-1">Bộ phận</label>
                  <MultiSelectDropdown<Department>
                    placeholder="Chọn bộ phận"
                    dataOptions={departments}
                    optionSelected={selectedDepts}
                    getOptionKey={(d) => d.id}
                    getOptionLabel={(d) => d.name}
                    onChange={(selected) => patchLevel(i, { department_ids: selected.map((d) => d.id) })}
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-400 block mb-1">Cá nhân chỉ định</label>
                  <SelectDropdownAsyncPaginate<ItemDebtPenaltyUser>
                    // defaultOptions chỉ được đọc lúc mount → dựng lại khi vừa tải cấu hình (reloadKey), KHÔNG theo
                    // từng lần chọn (dựng lại giữa chừng làm mất focus/đóng menu).
                    key={`item-debt-users-${i}-${reloadKey}`}
                    url={`${API_USER_ROUTER.API_LIST}`}
                    projectId={ApiConfig.PROJECT_ID.USER_SERVICE}
                    placeholder="Tìm nhân sự..."
                    isMulti
                    defaultOptions={lv.users}
                    onChange={(selected) =>
                      patchLevel(i, {
                        users: (selected || []).map((u) => ({ id: Number(u.id), name: String(u.name || "") })),
                      })
                    }
                    labelOption="name"
                    classNames="w-full"
                    params={{ status: 1 }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
